import { HTMLProps, ReactNode, useMemo, useState } from 'react'
import clsx from 'classnames'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { variantsHeadline } from '@/shared/constants/variants'
import { IPostCategory } from '@/interfaces/analytics'
import { IEventCategory } from '@/interfaces/events'
import style from './filters-bar.module.scss'

interface EventFiltersProps extends Omit<HTMLProps<HTMLDivElement>, 'onChange'> {
  activeFilter?: IPostCategory | IEventCategory | null
  filters: IPostCategory[] | IEventCategory[]
  onFilterChange: (filter: IPostCategory | IEventCategory | null) => void
  renderButton?: (props: React.HTMLProps<HTMLButtonElement | HTMLDivElement>) => ReactNode
  initFilterValue?: any
  withoutAll?: boolean
}

export interface FiltersBar {
  id: string | number
  name: string
}

export const FiltersBar = (props: EventFiltersProps) => {
  const { className, onFilterChange, filters, renderButton, withoutAll = false } = props
  const analyticsFilters = useTranslations('AnalyticsFilters')
  const initFilterValue = {
    id: 'all',
    name: analyticsFilters('all'),
    slug: 'all',
    color: '#135AD3',
    name_plural: analyticsFilters('all'),
  }
  const defaultFilterValue = withoutAll ? filters[0].id : initFilterValue.id
  const [activeFilter, setActiveFilter] = useState<string | number>(defaultFilterValue)
  const extendFilters = useMemo(() => {
    if (withoutAll) return filters

    return [initFilterValue, ...filters]
  }, [filters])

  return (
    <div className={clsx(style.container, className)}>
      {extendFilters.map(filter => (
        <motion.button
          key={filter.slug}
          variants={variantsHeadline}
          onClick={() => {
            if (filter?.id === initFilterValue.id) {
              onFilterChange(null)
            } else {
              onFilterChange(filter)
            }

            setActiveFilter(filter.id)
          }}
          className={clsx('text text_type_d', style.tab, {
            [style.active]: activeFilter === filter.id,
          })}
        >
          {filter.name}
        </motion.button>
      ))}

      {renderButton ? renderButton({ className: style.link }) : null}
    </div>
  )
}

export type CompareFilter<ItemType> = (item: ItemType, filter: IPostCategory) => boolean

export function useFiltersBar<ItemType>(
  items: ItemType[],
  comparator: CompareFilter<ItemType>,
  initFilterValue: IPostCategory
) {
  const [filter, setFilter] = useState<IPostCategory>(initFilterValue)

  const cards = useMemo(
    () => items.filter(item => comparator(item, filter) || filter?.id === 'all'),
    [filter, initFilterValue]
  ) as ItemType[]

  return {
    cards,
    filter,
    onFilterChange: setFilter,
  }
}
